import { Link as ReactRouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import { getGridStringOperators } from '@mui/x-data-grid-premium';

import { CHILD_ROW, PARENT_ROW } from 'components/pages/pre-screen/PatientDatabase/constants';
import { generateUrlByKey } from 'components/root/router';

import { scBlue100 } from '../../../../constants/systemColors';

const PATIENT_IDENTITY_FIELDS = new Set(['patient', 'patientId', 'age', 'heightIn', 'weightLbs', 'bmi']);
const PARENT_VALUE_FIELDS = new Set([...PATIENT_IDENTITY_FIELDS, 'medicalHistories', 'medications']);
const PARENT_FILTER_FIELDS = new Set(['medicalHistories', 'medications']);

export function mapToTreeColumns(columns) {
  return columns.map(column => {
    const originalValueGetter = column.valueGetter;
    const originalValueFormatter = column.valueFormatter;
    const originalRenderCell = column.renderCell;

    return {
      ...column,
      valueGetter: (value, row, columnDefinition, apiRef) =>
        valueGetterModifier(value, row, columnDefinition, apiRef, originalValueGetter, column),
      valueFormatter: (value, row, columnDefinition, apiRef) =>
        valueFormatterModifier(value, row, columnDefinition, apiRef, originalValueFormatter, column),
      ...(PATIENT_IDENTITY_FIELDS.has(column.field) && {
        getApplyQuickFilterFn: createQuickFilter()
      }),
      ...(PARENT_FILTER_FIELDS.has(column.field) && {
        filterOperators: createParentFilterOperators(column, originalValueGetter),
        getApplyQuickFilterFn: createParentQuickFilter(column, originalValueGetter)
      }),
      ...((originalRenderCell || column.field === 'patient') && {
        renderCell: params => renderCellModifier(params, column, originalRenderCell)
      })
    };
  });
}

function createParentQuickFilter(column, originalValueGetter) {
  return searchValue => {
    const applyQuickFilter = createQuickFilter()(searchValue);

    if (!applyQuickFilter) {
      return null;
    }

    return (value, row, currentColumnDefinition, apiRef) => {
      if (row.patientRowType !== CHILD_ROW) {
        return applyQuickFilter(value);
      }

      const parentValue = row.parentFilterValues?.[column.field];
      const resolvedParentValue = originalValueGetter
        ? originalValueGetter(parentValue, row, currentColumnDefinition, apiRef)
        : parentValue;

      return applyQuickFilter(resolvedParentValue);
    };
  };
}

function createParentFilterOperators(column, originalValueGetter) {
  return (column.filterOperators ?? getGridStringOperators()).map(operator => ({
    ...operator,
    getApplyFilterFn: (filterItem, columnDefinition) => {
      const applyFilter = operator.getApplyFilterFn(filterItem, columnDefinition);

      if (!applyFilter) {
        return null;
      }

      return (value, row, currentColumnDefinition, apiRef) => {
        if (row.patientRowType !== CHILD_ROW) {
          return applyFilter(value, row, currentColumnDefinition, apiRef);
        }

        const parentValue = row.parentFilterValues?.[column.field];
        const resolvedParentValue = originalValueGetter
          ? originalValueGetter(parentValue, row, currentColumnDefinition, apiRef)
          : parentValue;

        return applyFilter(resolvedParentValue, row, currentColumnDefinition, apiRef);
      };
    }
  }));
}

function valueGetterModifier(value, row, columnDefinition, apiRef, originalValueGetter, column) {
  if (shouldHideParentValue(row, column.field)) {
    return '';
  }

  if (isPatientColumnInParentRow(column, row.patientRowType)) {
    return `${getPatientName(row)} (${row.patientGroupCount})`;
  }

  return originalValueGetter ? originalValueGetter(value, row, columnDefinition, apiRef) : value;
}

function valueFormatterModifier(value, row, columnDefinition, apiRef, originalValueFormatter, column) {
  if (shouldHideCell(row, column.field)) {
    return '';
  }

  if (isPatientColumnInParentRow(column, row.patientRowType)) {
    return value;
  }

  return originalValueFormatter ? originalValueFormatter(value, row, columnDefinition, apiRef) : value;
}

function renderCellModifier(params, column, originalRenderCell) {
  if (shouldHideCell(params.row, column.field)) {
    return null;
  }

  const displayedValue = params.formattedValue ?? params.value;

  if (isPatientColumnInParentRow(column, params.row.patientRowType)) {
    return (
      <Link
        component={ReactRouterLink}
        to={generateUrlByKey('Patient Database.Patient Profile.Patient Details', {
          patientId: params.row.patientUniqueId
        })}
        target="_blank"
        rel="noopener noreferrer"
        title="Go to Patient Profile"
        underline="hover"
        sx={{
          color: scBlue100,
          '&:hover': {
            color: '#004f99'
          }
        }}
      >
        {displayedValue}
      </Link>
    );
  }

  return originalRenderCell ? originalRenderCell(params) : displayedValue;
}

function isPatientColumnInParentRow(column, patientRowType) {
  return column.field === 'patient' && patientRowType === PARENT_ROW;
}

function getPatientName(row) {
  return [row.firstName, row.lastName].filter(Boolean).join(' ') ?? '';
}

function shouldHideParentValue(row, field) {
  return row.patientRowType === PARENT_ROW && !PARENT_VALUE_FIELDS.has(field);
}

function shouldHideCell(row, field) {
  return shouldHideParentValue(row, field) || (row.patientRowType === CHILD_ROW && PATIENT_IDENTITY_FIELDS.has(field));
}

function createQuickFilter() {
  return searchValue => {
    if (!searchValue) {
      return null;
    }

    const normalizedSearchValue = String(searchValue).toLowerCase();
    return value =>
      value !== null &&
      String(value)
        .toLowerCase()
        .includes(normalizedSearchValue);
  };
}
